import { Button, ButtonColors } from '@chiroup/components/Button';
import { Loading } from '@chiroup/components/Loading';
import { Evaluation } from '@chiroup/core/types/Evaluation.type';
import { useForm } from '@chiroup/hooks/useForm';
import React, { useMemo } from 'react';
import { Route, Routes } from 'react-router-dom';
import { CustomizationListItem } from '../../../../../components/clinical-skills/CustomizationListItem.type';
import { ORTHOPEDIC_DEFAULT_VALUES } from '../../../../../constants';
import useEvaluations from '../../../../../hooks/useEvaluations';
import EvalSelect from '../../../../common/fields/selects/EvalSelect';
import RegionSelect from '../../../../common/fields/selects/RegionSelect';
import OrthoTest from './OrthoTest';
import VideoDescriptionModal from './VideoDescriptionModal';

type Props = {
  close?: () => void;
  value: {
    [key: string]: any;
  };
  evalsThatWeNeed: string[];
  onChange: (
    tab: string,
    key: string,
    val: {
      [key: string]: {
        typicallyPositive?: boolean;
        typicallyNegative?: boolean;
        left?: {
          pos?: boolean;
          neg?: boolean;
        };
        right?: {
          pos?: boolean;
          neg?: boolean;
        };
      };
    }
  ) => void;
};

const Orthopedic: React.FC<Props> = ({ close, value, onChange, evalsThatWeNeed }) => {
  const { value: valueEval, onChange: onChangeEval } = useForm<Evaluation>(
    ORTHOPEDIC_DEFAULT_VALUES,
    {}
  );
  const { data: dataEvals, isFetching: isFetchingEval } = useEvaluations({
    evalsThatWeNeed: evalsThatWeNeed.map(Number),
  });
  const typicallyPositive = useMemo(() => {
    return Object.values(value || {}).filter(evaluation => evaluation.typicallyPositive);
  }, [value]);

  const typicallyNegative = useMemo(() => {
    return Object.values(value || {}).filter(evaluation => evaluation.typicallyNegative);
  }, [value]);

  const other = useMemo(() => {
    return Object.values(value || {}).filter(
      evaluation => !evaluation.typicallyNegative && !evaluation.typicallyPositive
    );
  }, [value]);

  const addTest = (testId: number[]) => {
    const previousTests = { ...value };
    const newTestsObj = testId.reduce((obj: any, id) => {
      obj[id] = previousTests[id] || {
        ID: id,
      };

      return obj;
    }, {});
    onChange('', '', { value: newTestsObj });
  };

  const evaluationHash = useMemo(() => {
    return dataEvals?.data?.reduce((obj: any, item) => {
      obj[item.ID] = item;

      if (item?.parentID) {
        obj[item.parentID] = item;
      }

      return obj;
    }, {});
  }, [dataEvals]);

  const onChangePosOrNegTest = (testId: number, prop: string, val: any) => {
    const newValue = { ...value };

    if (prop === 'side' && val === newValue[testId][prop]) {
      val = '';
    }

    if ((prop === 'side' || prop === 'value' || prop === 'testNotes') && val !== '') {
      newValue[testId].touched = true;
    }

    newValue[testId][prop] = val;
    onChange('', '', { value: { ...newValue } });
  };

  const removeTest = (testId: number) => {
    const newValue = { ...value };
    delete newValue[testId];
    onChange('', '', { value: newValue });
  };

  const acceptSuggestions = () => {
    const newValue = { ...value };
    Object.keys(newValue).forEach(key => {
      const test = newValue[key];

      if (test.typicallyPositive) {
        newValue[key] = { ...newValue[key], value: 'positive', touched: true };
      } else if (test.typicallyNegative) {
        newValue[key] = { ...newValue[key], value: 'negative', touched: true };
      }
    });
    onChange('', '', { value: newValue });
  };

  const dontShowAcceptSuggestionsButton = useMemo(() => {
    return Object.keys(value || {}).some(key => {
      const test = value[key];

      return test.value;
    });
  }, [value]);

  return (
    <>
      {isFetchingEval ? (
        <Loading color="text-gray-400 mx-auto m-2" size={10} />
      ) : (
        <>
          <Button
            onClick={acceptSuggestions}
            text="Accept suggestions"
            color={ButtonColors.plainWithBorder}
            className="m-2"
            disabled={dontShowAcceptSuggestionsButton}
          />
          <div className="divide-y-2 divide-gray-400 dark:divide-dark-gray-900">
            <div className="divide-y divide-gray-300 dark:divide-dark-gray-700">
              {typicallyPositive?.map(test => {
                return (
                  <OrthoTest
                    key={test.ID}
                    testId={Number(test.ID)}
                    onChange={onChangePosOrNegTest.bind(null, Number(test.ID))}
                    value={test}
                    test={evaluationHash?.[test.ID]}
                    removeTest={removeTest}
                  />
                );
              })}
            </div>
            <div className="divide-y divide-gray-300 dark:divide-dark-gray-700">
              {typicallyNegative?.map(test => {
                return (
                  <OrthoTest
                    key={test.ID}
                    testId={Number(test.ID)}
                    onChange={onChangePosOrNegTest.bind(null, Number(test.ID))}
                    value={test}
                    test={evaluationHash?.[test.ID]}
                    removeTest={removeTest}
                  />
                );
              })}
            </div>
            <div className="divide-y divide-gray-300 dark:divide-dark-gray-700">
              {other?.map(test => {
                return (
                  <OrthoTest
                    key={test.ID}
                    testId={Number(test.ID)}
                    onChange={onChangePosOrNegTest.bind(null, Number(test.ID))}
                    value={test}
                    test={evaluationHash?.[test.ID]}
                    removeTest={removeTest}
                  />
                );
              })}
            </div>
          </div>

          <form>
            <div className="flex flex-col col-span-4 bg-gray-50 px-4 pt-4 pb-4 rounded-md gap-2 dark:bg-dark-gray-900">
              <div>
                <label className={['block text-sm font-medium leading-5 text-gray-900'].join(' ')}>
                  Add Evaluations
                </label>
              </div>
              <RegionSelect
                onChange={onChangeEval('regions')}
                value={valueEval.regions}
                limit={1}
                className="w-full"
              />

              <EvalSelect
                className="w-full"
                onChange={addTest}
                value={Object.keys(value || {}).map(Number)}
                label="Clinical Evaluations"
                region={valueEval.regions}
                evaluations={dataEvals?.data as unknown as CustomizationListItem[]}
              />
            </div>
          </form>
        </>
      )}
      {!!close && (
        <div className="flex justify-end">
          <Button
            onClick={close}
            text="Close"
            color={ButtonColors.plainWithBorder}
            className="mt-5"
          />
        </div>
      )}

      <Routes>
        <Route
          path={`/:id`}
          element={
            <VideoDescriptionModal isOpen={true} setModalOpen={() => {}} data={evaluationHash} />
          }
        />
      </Routes>
    </>
  );
};

export default Orthopedic;
